import type { PaletteMode } from '@mui/material'

import type { NodeType } from 'common/types/model'

import { primitives } from './primitives'
import { SemanticTokens, semanticTokens } from './semantic'

interface NodeTokens {
  fill: string
  selectedFill: string
}

type Scale8 = Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8, string>

/** Same naming rule as SemanticTokens; `node` is the only group outside the design system. */
export interface DomainTokens {
  node: Record<NodeType, NodeTokens>
  chart: {
    actual: string
    forecast: string
    target: string
    grid: string
    axis: string
    categorical: Scale8
  }
  data: {
    cell: { input: string; changed: string; locked: string; total: string }
    value: { negative: string; positive: string }
  }
  thumbnail: Scale8
  client: { accent: string; onAccent: string; accentSubtle: string }
}

const { base, blue, celeste, teal, yellow, neutral, green, red, diagram } = primitives.color

const NODE_FILLS: Record<NodeType, string> = {
  variable: diagram.variable,
  datareading: diagram.datareading,
  outputnode: diagram.outputnode,
  report: diagram.report,
  reportwizard: diagram.report,
  button: diagram.button,
  module: diagram.module,
  index: diagram.index,
  inputscalar: diagram.input,
  inputselector: diagram.input,
  inputdateselector: diagram.input,
  inputchoice: diagram.input,
  inputdataarray: diagram.input,
  inputcube: diagram.input,
  inputdataframe: diagram.input,
  form: diagram.input,
  text: 'transparent'
}

const THUMBNAILS: Scale8 = {
  1: blue[500],
  2: teal[600],
  3: blue[700],
  4: celeste[600],
  5: blue[600],
  6: teal[700],
  7: celeste[700],
  8: blue[800]
}

const shared = (tokens: SemanticTokens) => ({
  node: Object.fromEntries(
    Object.entries(NODE_FILLS).map(([type, fill]) => [
      type,
      { fill, selectedFill: tokens.action.ghost.bgActive }
    ])
  ) as Record<NodeType, NodeTokens>,
  thumbnail: THUMBNAILS,
  client: {
    accent: tokens.bg.brand,
    onAccent: base.white,
    accentSubtle: tokens.action.ghost.bgActive
  }
})

export const domainTokens: Record<PaletteMode, DomainTokens> = {
  light: {
    ...shared(semanticTokens.light),
    chart: {
      actual: blue[900],
      forecast: blue[300],
      target: blue[700],
      grid: celeste[100],
      axis: celeste[600],
      categorical: {
        1: blue[900],
        2: teal[300],
        3: blue[500],
        4: celeste[400],
        5: teal[700],
        6: blue[200],
        7: celeste[700],
        8: teal[500]
      }
    },
    data: {
      cell: { input: blue[50], changed: yellow[100], locked: neutral[50], total: celeste[100] },
      value: { negative: red[600], positive: green[700] }
    }
  },
  dark: {
    ...shared(semanticTokens.dark),
    chart: {
      actual: blue[300],
      forecast: blue[600],
      target: blue[200],
      grid: celeste[800],
      axis: celeste[400],
      categorical: {
        1: blue[300],
        2: teal[300],
        3: blue[500],
        4: celeste[400],
        5: teal[500],
        6: blue[200],
        7: celeste[200],
        8: teal[200]
      }
    },
    data: {
      cell: { input: blue[900], changed: yellow[900], locked: celeste[950], total: celeste[800] },
      value: { negative: red[400], positive: green[400] }
    }
  }
}
